import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Mail, Check, AlertCircle } from "lucide-react";
import { useFunnel } from "../context/FunnelContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const EmailReflectionCapture = ({ segment }) => {
  const { answers } = useFunnel();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      setStatus("error");
      setErrorMsg("Please enter your email address so we know where to send it.");
      return;
    }
    if (!EMAIL_RE.test(trimmed)) {
      setStatus("error");
      setErrorMsg("That email address doesn't look quite right. Please double-check it.");
      return;
    }
    setStatus("sending");
    try {
      await axios.post(`${API}/leads`, { email: trimmed, segment, quiz_answers: answers });
      setStatus("sent");
    } catch {
      setStatus("error");
      setErrorMsg("We couldn't send your reflection just now. Please try again in a moment.");
    }
  };

  if (status === "sent") {
    return (
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        data-testid="lead-capture-confirmation"
        className="mt-8 flex items-center gap-2 text-sm text-[#4A6B56]"
      >
        <Check className="w-4 h-4" /> Your reflection is on its way. Please check your inbox.
      </motion.p>
    );
  }

  return (
    <div className="mt-8">
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3" data-testid="lead-capture-form">
        <div className="flex items-center gap-2 flex-1 px-4 py-2.5 rounded-xl border border-[#4A6B56]/20 bg-white">
          <Mail className="w-4 h-4 text-[#4A6B56]/60 shrink-0" />
          <input
            data-testid="lead-email-input"
            type="text"
            inputMode="email"
            autoComplete="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (status === "error") setStatus("idle");
            }}
            placeholder="Not ready to book? Get this reflection by email"
            className="w-full bg-transparent outline-none text-sm text-[#232B25] placeholder:text-[#5F6A62]/70"
          />
        </div>
        <button
          type="submit"
          data-testid="lead-capture-submit-btn"
          disabled={status === "sending"}
          className="px-5 py-2.5 rounded-xl border border-[#4A6B56]/30 text-[#4A6B56] text-sm font-medium hover:bg-[#E2EAE4] transition-colors duration-300 disabled:opacity-60"
        >
          {status === "sending" ? "Sending..." : "Send it gently"}
        </button>
      </form>
      {status === "error" && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          data-testid="lead-capture-error"
          className="mt-3 flex items-center gap-2 text-sm text-[#A0453A]"
        >
          <AlertCircle className="w-4 h-4 shrink-0" /> {errorMsg}
        </motion.p>
      )}
    </div>
  );
};
